import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { authAPI } from '../../services/auth.api';
import { Button, Input } from '@/components/ui';

const forgotPasswordSchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address'),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

const ForgotPassword: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    setIsSubmitting(true);

    try {
      await authAPI.forgotPassword({ email: data.email });
      setEmailSent(true);
    } catch (error) {
      console.error('Forgot password error:', error);
      setEmailSent(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (emailSent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-meradio-primary/10 to-meradio-secondary/10 dark:from-meradio-primary/5 dark:to-meradio-secondary/5 flex items-center justify-center p-4">
        <div className="card max-w-md w-full text-center">
          <div className="mb-6">
            <div className="w-16 h-16 bg-meradio-primary/10 dark:bg-meradio-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-meradio-primary"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-meradio-dark dark:text-meradio-cream mb-2">
              Check your email
            </h1>
            <p className="text-meradio-dark-light dark:text-meradio-dark-light">
              If an account exists with that email address, we've sent you instructions to reset your password.
            </p>
          </div>

          <div className="space-y-3">
            <p className="text-sm text-meradio-dark-light dark:text-meradio-dark-light">
              Didn't receive the email? Check your spam folder or try again.
            </p>

            <Button variant="link" size="sm" onClick={() => setEmailSent(false)}>
              Try a different email
            </Button>
          </div>

          <div className="mt-6 pt-6 border-t border-meradio-dark/10 dark:border-meradio-cream/10">
            <Link
              to="/login"
              className="text-sm text-meradio-secondary hover:text-meradio-secondary-dark dark:hover:text-meradio-secondary transition-colors font-medium"
            >
              Back to login
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-meradio-primary/10 to-meradio-secondary/10 dark:from-meradio-primary/5 dark:to-meradio-secondary/5 flex items-center justify-center p-4">
      <div className="card max-w-md w-full">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold gradient-text mb-2">Reset Password</h1>
          <p className="text-meradio-dark-light dark:text-meradio-dark-light">
            Enter your email address and we'll send you instructions to reset your password.
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label
              htmlFor="email"
              className="block text-sm font-medium text-meradio-dark dark:text-meradio-cream mb-1"
            >
              Email Address
            </label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              placeholder="Enter your email"
              disabled={isSubmitting}
              error={errors.email?.message}
              {...register('email')}
            />
          </div>

          <Button type="submit" fullWidth isLoading={isSubmitting} disabled={isSubmitting}>
            {isSubmitting ? 'Sending...' : 'Send Reset Instructions'}
          </Button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-sm text-meradio-dark-light dark:text-meradio-dark-light">
            Remember your password?{' '}
            <Link
              to="/login"
              className="text-meradio-primary hover:text-meradio-primary-dark dark:hover:text-meradio-primary font-medium transition-colors"
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
